import CallbackFormT from '@factories/RowFactory/components/CallbackForm/types';
import React, { useCallback, useState } from 'react';
import { productController } from '@controllers/index';
import { Heading } from '@components/Atoms/Heading';
import { CheckoutFormRow } from '@components/Organisms/OrderForms/CheckoutFormRow';
import { Button, CheckBox } from '@components/Atoms';
import { renderImage, renderParagraphs } from '@lib/utils';
import StyledCheckoutCheckbox from '@components/Organisms/OrderForms/styled/StyledCheckoutCheckbox';
import { ButtonType } from '@components/Atoms/Button/types';
import StyledButtonRow from '@components/Organisms/OrderForms/styled/StyledButtonRow';
import { StyledFeatureInfoCardsSection } from '@factories/RowFactory/components/CallbackForm/styled/StyledCallbackFormSection';
import { Format } from '@lib/types';

export const CallbackForm = ({
  display,
  title,
  formTitle,
  consentCheckText,
  submitText,
  image,
}: CallbackFormT) => {
  const { customerCallMeServer } = productController;
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  const rows = [
    { key: 'contact_name', label: 'First Name', required: true, value: '' },
    { key: 'contact_surname', label: 'Last Name', required: true, value: '' },
    { key: 'email', label: 'Email', required: true, value: '' },
    { key: 'phone', label: 'Phone Number', required: true, value: '' },
    { key: 'postcode', label: 'Post code', required: true, value: '' },
    { key: 'accept_terms', type: 'checkbox', required: true, value: '' },
    { key: 'accept_marketing', type: 'checkbox', required: true, value: '' },
  ];

  const defaultFormData = rows.reduce((acc, row) => {
    return {
      ...acc,
      [row.key]: row.value,
    };
  }, {});

  const [formData, setFormData] = useState<any>(defaultFormData);

  const formDataValues: Array<{
    [key: string]: string;
  }> = Object.values(formData);

  const validators = [
    {
      keys: [],
      isValid: formDataValues.every((field) => field),
      message: 'Field is required',
    },
    {
      keys: ['email'],
      isValid: formData.email?.match(/^[^\s@]+@([^\s@.,]+\.)+[^\s@.,]{2,}$/),
      message: 'Email is invalid',
    },
    {
      keys: ['phone'],
      isValid: formData.phone?.match(
        /^(((\+44\s?\d{4}|\(?0\d{4}\)?)\s?\d{3}\s?\d{3})|((\+44\s?\d{3}|\(?0\d{3}\)?)\s?\d{3}\s?\d{4})|((\+44\s?\d{2}|\(?0\d{2}\)?)\s?\d{4}\s?\d{4}))(\s?#(\d{4}|\d{3}))?$/,
      ),
      message: 'Phone is invalid',
    },
  ];

  const isValid = validators.every((validator) => validator.isValid);

  const submitForm = useCallback(async () => {
    setHasSubmitted(true);

    try {
      await customerCallMeServer({ args: formData }).then((responseCallMe) => {
        console.log({ responseCallMe });
      });
    } catch (error) {
      console.log(error);
      setHasSubmitted(false);
    }
  }, [customerCallMeServer, formData]);

  const updateFormData = (key: string, value: any) => {
    setFormData((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  if (!display) return null;

  return (
    <StyledFeatureInfoCardsSection className="container">
      <header className="mb-10">
        <Heading level={2}>{title}</Heading>
      </header>
      <div className="wrapper">
        <div className="form-container">
          {hasSubmitted ? (
            <p className="submittedText">
              Thank you! Your request has been received. Our team will contact you shortly.
            </p>
          ) : (
            <>
              <h3>{formTitle}</h3>
              <form className="flex flex-col" onSubmit={submitForm}>
                {rows
                  .filter(({ type }) => type !== 'checkbox')
                  .map(({ key, label, type, required }) => (
                    <CheckoutFormRow
                      className="w-1/2"
                      key={key}
                      name={key}
                      label={label}
                      formData={formData}
                      validators={validators}
                      onChange={updateFormData}
                      type={type}
                      required={required}
                    />
                  ))}

                <StyledCheckoutCheckbox>
                  <CheckBox
                    value="Checkbox"
                    label={renderParagraphs({
                      body: '<span>I agree to the [Terms & Conditions](/legal)</span>',
                      hasMarkdown: true,
                    })}
                    name="accept_terms"
                    id="accept_terms"
                    checked={formData.accept_terms}
                    onClick={(value) => updateFormData('accept_terms', value)}
                  />
                </StyledCheckoutCheckbox>

                <StyledCheckoutCheckbox>
                  <CheckBox
                    value="CheckboxMarketing"
                    label={renderParagraphs({
                      body: consentCheckText,
                      hasMarkdown: true,
                    })}
                    name="accept_marketing"
                    id="accept_marketing"
                    checked={formData.accept_marketing}
                    onClick={(value) => updateFormData('accept_marketing', value)}
                  />
                </StyledCheckoutCheckbox>
                <StyledButtonRow className="mt-10">
                  <Button buttonType={ButtonType.PRIMARY} type="submit" disabled={!isValid}>
                    {submitText}
                  </Button>
                </StyledButtonRow>
              </form>
            </>
          )}
        </div>
        <div className="image-container">
          {image &&
            renderImage({
              className: 'image-shape',
              alternativeText: image.alternativeText,
              formats: image.formats,
              format: Format.LARGE,
            })}
        </div>
      </div>
    </StyledFeatureInfoCardsSection>
  );
};
